import { useCallback, useMemo, useState } from "react";
import type { Product, WorkshopService } from "@/types";
import type { PriceResult } from "@/lib/posPricing";

export interface CartItem {
  key: string;
  item_type: "product" | "service";
  product_id: string | null;
  service_id: string | null;
  name: string;
  unit: string | null;
  quantity: number;
  base_price: number;
  unit_price: number;
  discount: number;
  tax_rate: number;
  cost_price: number;
  stock: number;
  offer_name: string | null;
  offer_applied: boolean;
  wholesale: boolean;
}

export interface CartTotals {
  subtotal: number;
  discountTotal: number;
  taxTotal: number;
  total: number;
  itemCount: number;
  unitCount: number;
  costTotal: number;
  profit: number;
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

function lineKey(prefix: string, id: string): string {
  return `${prefix}:${id}`;
}

export function useCart() {
  const [items, setItems] = useState<CartItem[]>([]);

  const addProduct = useCallback((product: Product, price: PriceResult, stock: number) => {
    const key = lineKey("product", product.id);
    setItems((prev) => {
      const existing = prev.find((item) => item.key === key);
      if (existing) {
        return prev.map((item) =>
          item.key === key ? { ...item, quantity: round2(item.quantity + 1) } : item,
        );
      }
      const next: CartItem = {
        key,
        item_type: "product",
        product_id: product.id,
        service_id: null,
        name: product.name,
        unit: product.unit ?? "Unidad",
        quantity: 1,
        base_price: price.base,
        unit_price: price.final,
        discount: 0,
        tax_rate: Number(product.tax_rate ?? 0),
        cost_price: Number(product.cost_price ?? 0),
        stock,
        offer_name: price.offerName,
        offer_applied: price.offerApplied,
        wholesale: price.wholesale,
      };
      return [...prev, next];
    });
  }, []);

  const addService = useCallback((service: WorkshopService) => {
    const key = lineKey("service", service.id);
    setItems((prev) => {
      const existing = prev.find((item) => item.key === key);
      if (existing) {
        return prev.map((item) =>
          item.key === key ? { ...item, quantity: round2(item.quantity + 1) } : item,
        );
      }
      const next: CartItem = {
        key,
        item_type: "service",
        product_id: null,
        service_id: service.id,
        name: service.name,
        unit: "Servicio",
        quantity: 1,
        base_price: Number(service.price ?? 0),
        unit_price: Number(service.price ?? 0),
        discount: 0,
        tax_rate: 0,
        cost_price: 0,
        stock: 0,
        offer_name: null,
        offer_applied: false,
        wholesale: false,
      };
      return [...prev, next];
    });
  }, []);

  const setQuantity = useCallback((key: string, quantity: number) => {
    setItems((prev) =>
      prev.map((item) =>
        item.key === key ? { ...item, quantity: quantity > 0 ? round2(quantity) : 1 } : item,
      ),
    );
  }, []);

  const increment = useCallback((key: string) => {
    setItems((prev) =>
      prev.map((item) =>
        item.key === key ? { ...item, quantity: round2(item.quantity + 1) } : item,
      ),
    );
  }, []);

  const decrement = useCallback((key: string) => {
    setItems((prev) =>
      prev.map((item) =>
        item.key === key ? { ...item, quantity: Math.max(round2(item.quantity - 1), 1) } : item,
      ),
    );
  }, []);

  const setDiscount = useCallback((key: string, discount: number) => {
    setItems((prev) =>
      prev.map((item) => (item.key === key ? { ...item, discount: Math.max(discount, 0) } : item)),
    );
  }, []);

  const removeItem = useCallback((key: string) => {
    setItems((prev) => prev.filter((item) => item.key !== key));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  /** Restores a previously suspended cart. */
  const loadItems = useCallback((next: CartItem[]) => {
    setItems(Array.isArray(next) ? next : []);
  }, []);

  const totals = useMemo<CartTotals>(() => {
    let subtotal = 0;
    let discountTotal = 0;
    let taxTotal = 0;
    let costTotal = 0;
    let unitCount = 0;

    items.forEach((item) => {
      const gross = item.quantity * item.unit_price;
      const net = Math.max(gross - item.discount, 0);
      subtotal += gross;
      discountTotal += item.discount;
      taxTotal += round2((net * item.tax_rate) / 100);
      costTotal += item.quantity * item.cost_price;
      unitCount += item.quantity;
    });

    const total = subtotal - discountTotal + taxTotal;
    return {
      subtotal: round2(subtotal),
      discountTotal: round2(discountTotal),
      taxTotal: round2(taxTotal),
      total: round2(total),
      itemCount: items.length,
      unitCount,
      costTotal: round2(costTotal),
      profit: round2(total - taxTotal - costTotal),
    };
  }, [items]);

  return {
    items,
    totals,
    addProduct,
    addService,
    setQuantity,
    increment,
    decrement,
    setDiscount,
    removeItem,
    clear,
    loadItems,
  };
}